import React from 'react';
import {useNavigate} from 'react-router';

import {useCanvasApi} from 'apps/metric-tree/src/api/canvas';
import {useExperimentsStore} from 'apps/experiments/src/store';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {useFeatureFlagsApi} from 'apps/feature-flags/src/api/feature-flags';
import {deleteContent} from 'common/report/queries/app-api/content-client';
import {useTranslation} from 'common/i18n/hooks';
import {Content} from 'common/types/app-api/content';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {useExperimentList} from 'apps/experiments/src/components/hooks/experiment-list';

import {TrackingEvent, TrackingSource} from '../tracking';
import {getContentTypeName, getHomeUrl, performContentAction} from '../helpers';

export type UseDeleteContentParams = {
  projectId: string | number;
  workspaceId?: string | number;
  returnHomeAfterSuccess?: boolean;
  source?: TrackingSource;
};

export function useDeleteContent({projectId, workspaceId, returnHomeAfterSuccess, source}: UseDeleteContentParams) {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const {experiments, setExperiments} = useExperimentList();
  const setAllowNavigatingAway = useExperimentsStore((state) => state.setAllowNavigatingAway);

  const {deleteFeatureFlag} = useFeatureFlagsApi({projectId, workspaceId});
  const {deleteCanvas} = useCanvasApi({projectId, workspaceId});

  return React.useCallback(
    (content: Content, onSuccess?: (contentId: string) => void) => {
      const entityTypeName = getContentTypeName(content, i18n);
      return performContentAction({
        content,
        action: () => {
          if (content.content_type === SharableEntityType.FeatureFlag) {
            return deleteFeatureFlag({id: content.id}) as Promise<Content>;
          } else if (content.content_type === SharableEntityType.Experiment) {
            setAllowNavigatingAway(true);
          }
          if (content.content_type === SharableEntityType.MetricTree) {
            return deleteCanvas({id: content.id}) as Promise<Content>;
          }
          return deleteContent({contentId: content.id, projectId});
        },
        onSuccess: ({id}) => {
          onSuccess?.(id);
          switch (content.content_type) {
            case SharableEntityType.Experiment: {
              setExperiments((experiments ?? []).filter((exp) => exp.id !== id));
              break;
            }
            case SharableEntityType.FeatureFlag: {
              break;
            }
          }
          if (returnHomeAfterSuccess) {
            const homeUrl = getHomeUrl(content.content_type, {projectId, workspaceId});
            navigate(homeUrl);
          }
        },
        onError: (err) => {
          if (content.content_type === SharableEntityType.Experiment) {
            setAllowNavigatingAway(false);
          }
          if (err.message === `Cannot delete free trial experiment`) {
            addToast({
              message: i18n.t(`sidenav.delete-experiment-on-trial-error`),
              type: ToastType.ERROR,
            });
          }
        },
        successMessage: i18n.t(`sidenav.delete-content`, {entityTypeName, name: content.name}),
        errorMessage: i18n.t(`sidenav.delete-content-error`, {entityTypeName, name: content.name}),
        successTrackingEvent: TrackingEvent.DeleteContent,
        errorTrackingEvent: TrackingEvent.DeleteContent,
        source,
      });
    },
    [
      i18n,
      navigate,
      projectId,
      workspaceId,
      returnHomeAfterSuccess,
      source,
      deleteFeatureFlag,
      deleteCanvas,
      setExperiments,
      experiments,
    ],
  );
}
